import {
  type AutoApprover,
  EscalationApprovalRule,
} from "@p0security/shared/types/workflow/types";
import { Select } from "antd";
import { BaseOptionType } from "antd/lib/select";
import { useAuthFetch } from "components/Login/hook";
import {
  StyledFormItem,
  standardFormItemCols,
} from "components/common/forms/styles";
import { useAutoSelectSingleOption } from "hooks/useAutoSelectSingleOption";
import { startCase } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useFirestoreCollection } from "../../../../providers/FirestoreProvider.tsx";
import { ErrorDisplay } from "../../../Error.tsx";

type IncidentioSchedule = {
  id: string;
  name: string;
  timezone: string;
};

const labels: Record<AutoApprover, string> = {
  incidentio: "Incident.io",
  pagerduty: "PagerDuty",
};

const selectLabels: Record<AutoApprover, string> = {
  incidentio: "schedule",
  pagerduty: "service",
};

export const EscalationApproval: React.FC<{
  value: EscalationApprovalRule;
  installed: AutoApprover[];
  onChange: (rule: EscalationApprovalRule) => void;
}> = ({ installed, value, onChange }) => {
  const [error, setError] = useState<any>();
  const authFetch = useAuthFetch(setError);

  // Refresh schedules from approver once when the component mounts so that
  // the Firestore cache is up-to-date before the user interacts with the picker.
  useEffect(() => {
    const approver = value.integration;
    authFetch(`integrations/${approver}/refresh`, { method: "POST" });
  }, [value.integration, authFetch]);

  const { docs } = useFirestoreCollection<IncidentioSchedule>(
    `integrations/${value.integration}/${selectLabels[value.integration]}s`,
    { live: true, tenantAware: true }
  );

  const options = useMemo(
    () =>
      docs?.map((s) => (
        <Select.Option key={s.data.id}>{s.data.name}</Select.Option>
      )) ?? [],
    [docs]
  );

  const label = selectLabels[value.integration];

  const services = useMemo(() => value.services ?? [], [value]);

  const onUpdate = useMemo(
    () => async (selected: string[]) => {
      onChange({ ...value, services: selected });
    },
    [onChange, value]
  );

  const filterSchedule = useCallback(
    (input: string, option?: BaseOptionType) =>
      option?.children
        ?.toString()
        .toLowerCase()
        ?.includes(input.toLowerCase()) ?? false,
    []
  );

  const installedOptions = useMemo(
    () => installed.map((key) => ({ value: key, label: labels[key] })),
    [installed]
  );

  const handleSelectApprover = useCallback(
    (integration: AutoApprover) => {
      onChange({ type: "escalation", integration, services: [] });
    },
    [onChange]
  );

  const { locked: integrationLocked } = useAutoSelectSingleOption(
    installedOptions,
    value.integration,
    handleSelectApprover
  );

  return (
    <>
      {error && <ErrorDisplay error={error} />}
      <StyledFormItem {...standardFormItemCols} label="Integration">
        <Select<AutoApprover>
          className={integrationLocked ? "auto-locked-select" : undefined}
          options={installedOptions}
          onSelect={handleSelectApprover}
          value={value.integration}
          disabled={integrationLocked}
        />
      </StyledFormItem>
      <StyledFormItem
        {...standardFormItemCols}
        label={`${startCase(label)}`}
        rules={[
          {
            required: true,
            message: `Please select at least one ${label}`,
          },
        ]}
      >
        <Select
          allowClear
          filterOption={filterSchedule}
          mode="multiple"
          onChange={onUpdate}
          placeholder={`Select a ${label}`}
          style={{ flexGrow: 1 }}
          value={services}
        >
          {options}
        </Select>
      </StyledFormItem>
    </>
  );
};
